"use client"

import { useEffect, useMemo, useState } from "react"
import { usePathname } from "next/navigation"
import { Check, Copy, Download, Mail } from "lucide-react"
import OwnerHeader from "@/components/ownerHeader"
import { useSimplePet } from "@/context/SimplePetContext"
import {
  PLAQUE_FONT_PRESETS,
  PLAQUE_ICON_PATHS,
  PLAQUE_LIMITS,
  PLAQUE_PHRASE_SUGGESTIONS,
  PLAQUE_SUBTITLE_SUGGESTIONS,
  PLAQUE_SPEC,
  encodePlaqueParams,
  estimatePlaqueTextWidth,
  formatPlaqueDates,
  normalizePlaqueDesign,
  type PlaqueDesign,
  type PlaqueFontPreset,
  type PlaqueIcon,
  type PlaqueMeasure,
} from "@/lib/plaqueDesign"
import PlaquePreview from "./PlaquePreview"
import { PLAQUE_BROWSER_FONTS, measurePlaqueTextInBrowser } from "./plaqueFonts"

const DESIGN_EMAIL = "support@petaftercare.co.nz"

const labelCls = "block text-xs uppercase tracking-[0.15em] text-[#5C534B] mb-2"
const inputCls =
  "w-full rounded-sm border border-[#D7CBB3] bg-white px-3 py-2.5 text-[15px] text-[#1B1714] placeholder:text-[#5C534B]/50 focus:outline-none focus:ring-2 focus:ring-[#b99a6b]/50"
const suggestionCls =
  "rounded-full border border-[#E5DCCB] bg-[#FAF9F6] px-2.5 py-1 text-xs text-[#5C534B] hover:border-[#9B7B4D] hover:text-[#1B1714]"
const chipCls = (active: boolean) =>
  `rounded-sm border px-3 py-1.5 text-sm transition-colors ${
    active ? "border-[#1B1714] bg-[#1B1714] text-white" : "border-[#D7CBB3] bg-white text-[#1B1714] hover:border-[#9B7B4D]"
  }`

const ICON_LABELS: Record<PlaqueIcon, string> = { paw: "Paw", heart: "Heart", line: "Plain line", none: "None" }

/** Lets the date inputs switch between year and full-date entry without losing what was typed. */
function toYear(v: string) {
  return v.slice(0, 4)
}

export default function PlaqueDesigner({ initialDesign }: { initialDesign: PlaqueDesign }) {
  const pathname = usePathname()
  const { currentPet } = useSimplePet()
  const [design, setDesign] = useState<PlaqueDesign>(initialDesign)
  const [measure, setMeasure] = useState<PlaqueMeasure>(() => estimatePlaqueTextWidth)
  const [copied, setCopied] = useState(false)
  const [origin, setOrigin] = useState("")

  // Switch from estimated to real text widths once the plaque fonts are in.
  useEffect(() => {
    setOrigin(window.location.origin)
    let cancelled = false
    const faces = Object.values(PLAQUE_BROWSER_FONTS)
    Promise.all(faces.map((f) => document.fonts.load(`${f.fontStyle} ${f.fontWeight} 16px ${f.fontFamily}`)))
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setMeasure(() => measurePlaqueTextInBrowser)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Raw input lives in state so typing (trailing spaces, half-typed years) isn't
  // fought; `clean` below is what the preview, URL and print file use.
  const update = (patch: Partial<PlaqueDesign>) => setDesign((d) => ({ ...d, ...patch }))

  // What actually goes on the plate — trimmed/clamped.
  const clean = useMemo(() => normalizePlaqueDesign(design), [design])
  const query = useMemo(() => encodePlaqueParams(clean), [clean])

  // Keep the address bar as the shareable design. history.replaceState (not
  // router.replace) so typing doesn't trigger a server round-trip per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      window.history.replaceState(null, "", query ? `${pathname}?${query}` : pathname)
    }, 300)
    return () => clearTimeout(t)
  }, [query, pathname])

  const isEmpty = !clean.name
  const previewDesign = isEmpty ? { ...clean, name: "Your pet's name" } : clean

  const shareUrl = `${origin}${pathname}${query ? `?${query}` : ""}`
  const printUrl = `/api/plaque/svg?${query}`
  const dates = formatPlaqueDates(clean)
  const mailto = `mailto:${DESIGN_EMAIL}?subject=${encodeURIComponent(
    `Plaque design for ${clean.name || "my pet"}`
  )}&body=${encodeURIComponent(
    [
      `Hi PetAftercare,`,
      ``,
      `Here's the plaque I'd like for ${clean.name || "my pet"}:`,
      ``,
      `Name: ${clean.name}`,
      clean.subtitle ? `Subtitle: ${clean.subtitle}` : null,
      clean.phrase ? `Phrase: ${clean.phrase}` : null,
      dates ? `Dates: ${dates}` : null,
      `Style: ${PLAQUE_FONT_PRESETS[clean.font].label}, divider: ${ICON_LABELS[clean.icon].toLowerCase()}`,
      ``,
      `Design link: ${shareUrl}`,
    ]
      .filter((l) => l !== null)
      .join("\n")
  )}`

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt("Copy this link", shareUrl)
    }
  }

  const dateInputType = design.dateStyle === "years" ? "number" : "date"

  return (
    <>
      <OwnerHeader />
      <div
        className={`min-h-screen bg-[#FAF9F6] mt-[110px] sm:mt-[90px] pt-[40px] sm:pt-[30px] pb-16 sm:pb-12 border border-t-[#979797] ${
          currentPet ? "mt-[160px] sm:mt-[140px]" : ""
        }`}
      >
        <div className="mx-auto px-4 max-w-6xl">
          <div className="mb-8 sm:mb-6 max-w-2xl">
            <h1 className="font-serif italic text-[#1B1714] text-4xl md:text-3xl leading-tight mb-3">Design a plaque</h1>
            <p className="text-[15px] text-[#5C534B] leading-relaxed">
              A brushed-gold plaque ({PLAQUE_SPEC.widthMm} × {PLAQUE_SPEC.heightMm} mm) engraved with your pet&rsquo;s name, a
              few words and their dates. It fits on our urns and keepsake boxes.
            </p>
          </div>

          <div className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:grid-cols-1 gap-10 sm:gap-6 items-start">
            {/* Preview */}
            <div className="sticky top-[130px] lg:static">
              <div
                className="rounded-lg px-10 py-14 sm:px-4 sm:py-8"
                style={{
                  // Dark timber, like the urn the plaque is fixed to.
                  background:
                    "repeating-linear-gradient(96deg, rgba(0,0,0,0.07) 0 2px, transparent 2px 9px), repeating-linear-gradient(94deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 23px), linear-gradient(135deg, #6b2f1d, #4a1d12 55%, #5c2618)",
                }}
              >
                <PlaquePreview design={previewDesign} measure={measure} placeholder={isEmpty} className="w-full h-auto" />
              </div>
              <p className="mt-3 text-xs text-[#5C534B]/80 text-center">
                Shown at true proportions. We&rsquo;ll confirm the wording with you before engraving.
              </p>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg border border-[#E5DCCB] p-7 sm:p-5 space-y-6">
              <div>
                <label htmlFor="plaque-name" className={labelCls}>
                  Pet&rsquo;s name
                </label>
                <input
                  id="plaque-name"
                  className={inputCls}
                  value={design.name}
                  maxLength={PLAQUE_LIMITS.name}
                  placeholder="e.g. Bella"
                  autoComplete="off"
                  onChange={(e) => update({ name: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="plaque-subtitle" className={labelCls}>
                  Short line under the name <span className="normal-case tracking-normal text-[#5C534B]/70">(optional)</span>
                </label>
                <input
                  id="plaque-subtitle"
                  className={inputCls}
                  value={design.subtitle}
                  maxLength={PLAQUE_LIMITS.subtitle}
                  placeholder="e.g. A true gentleman"
                  autoComplete="off"
                  onChange={(e) => update({ subtitle: e.target.value })}
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {PLAQUE_SUBTITLE_SUGGESTIONS.map((p) => (
                    <button key={p} type="button" onClick={() => update({ subtitle: p })} className={suggestionCls}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="plaque-phrase" className={labelCls}>
                  A few words <span className="normal-case tracking-normal text-[#5C534B]/70">(optional)</span>
                </label>
                <textarea
                  id="plaque-phrase"
                  className={inputCls + " resize-none"}
                  rows={2}
                  value={design.phrase}
                  maxLength={PLAQUE_LIMITS.phrase}
                  placeholder="e.g. Forever loyal, forever loved, forever in our hearts."
                  onChange={(e) => update({ phrase: e.target.value.replace(/\s*\n\s*/g, " ") })}
                />
                <p className="mt-1 text-xs text-[#5C534B]/80">Longer wording is set over two lines.</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {PLAQUE_PHRASE_SUGGESTIONS.map((p) => (
                    <button key={p} type="button" onClick={() => update({ phrase: p })} className={suggestionCls}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={labelCls + " mb-0"}>
                    Dates <span className="normal-case tracking-normal text-[#5C534B]/70">(optional)</span>
                  </span>
                  <div className="flex gap-1">
                    {(["years", "full"] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={chipCls(design.dateStyle === s) + " !px-2.5 !py-1 !text-xs"}
                        onClick={() =>
                          update({
                            dateStyle: s,
                            // Years-only drops the day/month; full keeps a year until a date is picked.
                            dateFrom: s === "years" ? toYear(design.dateFrom) : design.dateFrom,
                            dateTo: s === "years" ? toYear(design.dateTo) : design.dateTo,
                          })
                        }
                      >
                        {s === "years" ? "Years only" : "Full dates"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      ["dateFrom", "Born"],
                      ["dateTo", "Passed"],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="block">
                      <span className="block text-xs text-[#5C534B] mb-1">{label}</span>
                      <input
                        type={dateInputType}
                        className={inputCls}
                        inputMode={dateInputType === "number" ? "numeric" : undefined}
                        min={dateInputType === "number" ? 1900 : "1900-01-01"}
                        max={dateInputType === "number" ? 2100 : "2100-12-31"}
                        placeholder={dateInputType === "number" ? "YYYY" : undefined}
                        // A year-only value can't show in a date input; leave it blank until picked.
                        value={dateInputType === "date" && design[key].length === 4 ? "" : design[key]}
                        onChange={(e) => update({ [key]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
                {dates && <p className="mt-2 text-xs text-[#5C534B]">Engraved as: {dates}</p>}
              </div>

              <div>
                <span className={labelCls}>Lettering</span>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(PLAQUE_FONT_PRESETS) as PlaqueFontPreset[]).map((key) => {
                    const preset = PLAQUE_FONT_PRESETS[key]
                    const f = PLAQUE_BROWSER_FONTS[preset.name.face]
                    const sample = clean.name || "Bella"
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => update({ font: key })}
                        aria-pressed={design.font === key}
                        className={`rounded-sm border px-2 py-3 text-center transition-colors ${
                          design.font === key ? "border-[#1B1714] ring-1 ring-[#1B1714]" : "border-[#D7CBB3] hover:border-[#9B7B4D]"
                        }`}
                      >
                        <span
                          className="block truncate text-[#1B1714] leading-tight"
                          style={{
                            fontFamily: f.fontFamily,
                            fontWeight: f.fontWeight,
                            fontStyle: f.fontStyle,
                            fontSize: key === "script" ? 26 : key === "modern" ? 15 : 18,
                            letterSpacing: `${preset.name.tracking}em`,
                            textTransform: preset.name.uppercase ? "uppercase" : undefined,
                          }}
                        >
                          {sample}
                        </span>
                        <span className="mt-1 block text-[11px] uppercase tracking-[0.15em] text-[#5C534B]">{preset.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <span className={labelCls}>Divider under the name</span>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(ICON_LABELS) as PlaqueIcon[]).map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => update({ icon })}
                      aria-pressed={design.icon === icon}
                      className={chipCls(design.icon === icon) + " inline-flex items-center gap-1.5"}
                    >
                      {(icon === "paw" || icon === "heart") && (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
                          <path d={PLAQUE_ICON_PATHS[icon]} />
                        </svg>
                      )}
                      {ICON_LABELS[icon]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#E5DCCB] pt-6 space-y-3">
                <p className="text-sm text-[#5C534B] leading-relaxed">
                  Happy with it? Send the design to us, or copy the link and give it to your vet.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={isEmpty ? undefined : mailto}
                    aria-disabled={isEmpty}
                    className={`inline-flex items-center gap-2 rounded-sm px-4 py-2.5 text-sm font-medium transition-colors ${
                      isEmpty
                        ? "bg-[#1B1714]/30 text-white cursor-not-allowed"
                        : "bg-[#1B1714] text-white hover:bg-[#3a312a]"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Email this design to us
                  </a>
                  <button
                    type="button"
                    onClick={copyLink}
                    disabled={isEmpty}
                    className="inline-flex items-center gap-2 rounded-sm border border-[#D7CBB3] bg-white px-4 py-2.5 text-sm text-[#1B1714] hover:border-[#9B7B4D] disabled:opacity-50"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Link copied" : "Copy link"}
                  </button>
                </div>
                {!isEmpty && (
                  <a
                    href={printUrl}
                    className="inline-flex items-center gap-1.5 text-xs text-[#5C534B] underline-offset-2 hover:underline"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download print file (SVG)
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
